import { useEffect, useState } from 'react';
import { secureGetItem } from '@/lib/storage';
import { useTheme } from '@/contexts/ThemeContext';
import { useAppData } from '@/contexts/AppDataContext';
import configData from '@/data/config.json';

export const LoadingScreen = () => {
  const { colors } = useTheme();
  const { config } = useAppData();
  // Initialize with default config icon if available, or empty string
  const [logoUrl, setLogoUrl] = useState(configData?.icon?.light || '');

  const primaryColor = colors.primary || '#3b82f6';

  // Convert hex to rgb for opacity effects
  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 59, g: 130, b: 246 }; // fallback blue-500
  };

  const rgb = hexToRgb(primaryColor);

  // Load logo on mount from cache (for first load) - runs immediately
  useEffect(() => {
    const loadCachedLogo = async () => {
      // First, try to get logo from config context (if already loaded)
      if (config?.icon?.light) {
        setLogoUrl(config.icon?.light);
        return;
      }

      // Fallback: Try to get logo from localStorage (from previous sessions)
      // This is critical for first-time loads where config hasn't loaded yet
      const cachedConfig = await secureGetItem('cached-config');
      if (cachedConfig) {
        try {
          const parsedConfig = JSON.parse(cachedConfig);
          if (parsedConfig?.icon?.light) {
            setLogoUrl(parsedConfig.icon?.light);
          }
        } catch (error) {
          console.error('Error parsing cached config:', error);
        }
      }
    };

    // Load immediately, don't wait
    loadCachedLogo();
  }, []); // Run only on mount

  // Update logo when config changes (this handles the case when config loads after mount)
  useEffect(() => {
    if (config?.icon?.light && config.icon?.light !== logoUrl) {
      setLogoUrl(config.icon?.light);
    }
  }, [config, logoUrl]);

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-slate-50 via-white to-slate-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 flex items-center justify-center z-50 overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute top-1/4 -left-20 w-96 h-96 rounded-full blur-3xl animate-blob"
          style={{ backgroundColor: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.05)` }}
        />
        <div
          className="absolute top-1/3 -right-20 w-96 h-96 rounded-full blur-3xl animate-blob animation-delay-2000"
          style={{ backgroundColor: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.04)` }}
        />
        <div
          className="absolute -bottom-20 left-1/3 w-96 h-96 rounded-full blur-3xl animate-blob animation-delay-4000"
          style={{ backgroundColor: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.03)` }}
        />
      </div>

      {/* Main Content */}
      <div className="relative flex flex-col items-center gap-6 px-4">
        {/* Logo Container with Glow Effect */}
        <div className="relative w-24 h-24 flex items-center justify-center">
          {/* Glow rings - Fixed dimensions to prevent layout shift */}
          <div className="absolute inset-0 -m-4 w-32 h-32">
            <div
              className="w-full h-full rounded-full blur-xl animate-pulse-slow"
              style={{
                background: `radial-gradient(circle, rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.2) 0%, rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.1) 100%)`
              }}
            />
          </div>
          <div className="absolute inset-0 -m-8 w-40 h-40">
            <div
              className="w-full h-full rounded-full blur-2xl animate-pulse-slower"
              style={{
                background: `radial-gradient(circle, rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.15) 0%, rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.05) 100%)`
              }}
            />
          </div>

          {/* Logo - Fixed dimensions to prevent layout shift */}
          {logoUrl ? (
            <img
              src={logoUrl}
              alt="Logo"
              className="h-24 w-24 object-contain rounded-2xl shadow-2xl backdrop-blur-sm dark:border-slate-800/50"
              width={96}
              height={96}
              onError={(e) => {
                // If image fails to load, hide it and show placeholder
                e.currentTarget.style.display = 'none';
                setLogoUrl('');
              }}
            />
          ) : (
            <div className="relative bg-white dark:bg-slate-900 rounded-2xl shadow-2xl p-4 backdrop-blur-sm border border-slate-200/50 dark:border-slate-800/50 w-24 h-24 flex items-center justify-center">
              <div
                className="h-20 w-20 rounded-xl animate-pulse"
                style={{
                  background: `linear-gradient(135deg, ${primaryColor} 0%, rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.8) 100%)`
                }}
              />
            </div>
          )}
        </div>

        {/* Loading Content */}
        <div className="flex flex-col items-center gap-6">
          {/* Text */}
          <div className="flex flex-col items-center gap-3">
            {/* Animated dots */}
            <div className="flex gap-1.5">
              <div
                className="w-2 h-2 rounded-full animate-bounce"
                style={{ backgroundColor: primaryColor, animationDelay: '0ms' }}
              />
              <div
                className="w-2 h-2 rounded-full animate-bounce"
                style={{ backgroundColor: primaryColor, animationDelay: '150ms' }}
              />
              <div
                className="w-2 h-2 rounded-full animate-bounce"
                style={{ backgroundColor: primaryColor, animationDelay: '300ms' }}
              />
            </div>


          </div>
        </div>
      </div>

      <style>{`
        @keyframes blob {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
        }

        .animate-blob {
          animation: blob 7s ease-in-out infinite;
        }

        .animation-delay-2000 {
          animation-delay: 2s;
        }

        .animation-delay-4000 {
          animation-delay: 4s;
        }

        .animate-pulse-slow {
          animation: pulse 3s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }

        .animate-pulse-slower {
          animation: pulse 4s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>
    </div>
  );
};
